<script setup>
import { onMounted, onBeforeUnmount, watch, nextTick, ref } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  tooltipText: {
    type: String,
  },
  hoverEvent: {
    default: null,
    required: true,
  },
  tooltipLeftShift: {
    type: Number,
    default: 0
  },
  tooltipTopShift: {
    type: Number,
    default: 0
  },
  tooltipDelay: {
    type: Number,
    default: 0,
  },
  tooltipTail: {
    type: Boolean,
    default: true,
  },
  tooltipPosition: {
    type: String,
    default: 'top',
  }
});
const store = useStore();

const emit = defineEmits(['pageScroll', 'mouseenter', 'mouseleave'])

const scrollListener = (event) => {
  emit('pageScroll');
  store.dispatch('tooltip_store/CLEAR_VIEW_TOOLTIP');
}

document.addEventListener('scroll', scrollListener, true);

const refTooltip = ref(null)
const tooltipLeftCoordinate = ref(0);
const tooltipTopCoordinate = ref(0);
const tailLeftCoordinate = ref(0);
const tailTopCoordinate = ref(0);
const tooltipTimeoutId = ref(null);
const isTooltipReady = ref(false);
const isImageReady = ref(false);

const checkifFitsViewport = (tooltipRect) => {
  // Проверка, выходит ли тултип за правую границу
  if (tooltipLeftCoordinate.value + tooltipRect.width > window.innerWidth) {
    tooltipLeftCoordinate.value = window.innerWidth - tooltipRect.width - 10;
  }
  // Проверка, выходит ли тултип за левую границу
  if (tooltipLeftCoordinate.value < 0) {
    tooltipLeftCoordinate.value = 10;
  }
}

const updateCoordinates = (event) => {
  const targetElementRect = event.target.getBoundingClientRect();
  const tooltipElement = refTooltip.value;
  
  const tooltipRect = tooltipElement.getBoundingClientRect();
  if (props.tooltipPosition === 'top') {
    tooltipLeftCoordinate.value = targetElementRect.left + (targetElementRect.width - tooltipRect.width) / 2 + props.tooltipLeftShift;
    tooltipTopCoordinate.value = targetElementRect.top - tooltipRect.height - 15 + props.tooltipTopShift;
    checkifFitsViewport(tooltipRect);
    tailLeftCoordinate.value = targetElementRect.left + (targetElementRect.width / 2) + props.tooltipLeftShift - 7;
    tailTopCoordinate.value = targetElementRect.top + props.tooltipTopShift - 16;
  }
  if (props.tooltipPosition === 'bottom') {
    tooltipLeftCoordinate.value = targetElementRect.left + (targetElementRect.width - tooltipRect.width) / 2 + props.tooltipLeftShift;
    tooltipTopCoordinate.value = targetElementRect.bottom + 15 + props.tooltipTopShift;
    checkifFitsViewport(tooltipRect);
    tailLeftCoordinate.value = targetElementRect.left + (targetElementRect.width / 2) - 6 + props.tooltipLeftShift;
    tailTopCoordinate.value = targetElementRect.bottom + 5 + props.tooltipTopShift;
  }
  if (props.tooltipPosition === 'right') {
    tooltipLeftCoordinate.value = targetElementRect.right + 15 + props.tooltipLeftShift;
    tooltipTopCoordinate.value = targetElementRect.top + (targetElementRect.height - tooltipRect.height) / 2 + props.tooltipTopShift;
    tailLeftCoordinate.value = targetElementRect.right + 3 + props.tooltipLeftShift;
    tailTopCoordinate.value = targetElementRect.top + (targetElementRect.height / 2) - 6 + props.tooltipTopShift;
  } 
  if (props.tooltipPosition === 'left') {
    tooltipLeftCoordinate.value = targetElementRect.left - tooltipRect.width - 15 + props.tooltipLeftShift;
    tooltipTopCoordinate.value = targetElementRect.top + (targetElementRect.height - tooltipRect.height) / 2 + props.tooltipTopShift;
    tailLeftCoordinate.value = targetElementRect.left - 17 + props.tooltipLeftShift;
    tailTopCoordinate.value = targetElementRect.top + (targetElementRect.height / 2) - 6 + props.tooltipTopShift;
  }
  isTooltipReady.value = true;
}

const tooltipUpdate = (event) => {
  if (!event) {
    return;
  }
  if (props.tooltipDelay != 0) {
    tooltipTimeoutId.value = setTimeout(() => {
      updateCoordinates(event);
    }, props.tooltipDelay);
  } else {
    updateCoordinates(event);
  }
}

onMounted(() => {
  tooltipUpdate(props.hoverEvent);
})

watch(() => props.hoverEvent, () => {
  clearTimeout(tooltipTimeoutId.value);
  nextTick(() => {
    tooltipUpdate(props.hoverEvent);
  })
})

onBeforeUnmount(() => {
  clearTimeout(tooltipTimeoutId.value);
  document.removeEventListener('scroll', scrollListener, true);
})

</script>

<template>
  <div
    @mouseenter="emit('mouseenter', $event)"
    @mouseleave="emit('mouseleave', $event)"
    class="restyle-tooltip"
    :style="{
      left: tooltipLeftCoordinate + 'px',
      top: tooltipTopCoordinate + 'px',
      visibility: isTooltipReady && (isImageReady || !props.tooltipTail) ? 'visible' : 'hidden'
    }"
    ref="refTooltip"
  >
    <slot name="tooltip-text">
      <span v-html="tooltipText"></span>
    </slot>
    <img
      v-if="props.tooltipTail"
      @load="isImageReady = true"
      src="@/assets/restyle/images/shared/tooltip/tooltip_tail.svg"
      :class="[props.tooltipPosition ? `tooltip-tail--${props.tooltipPosition}`: '']"
      :style="{ 'left': tailLeftCoordinate + 'px', 'top': tailTopCoordinate + 'px' }"
      class="tooltip-tail"
      alt=""
    >
  </div>
</template>

<style lang="scss" scoped>
  .restyle-tooltip {
    padding: 8px 11px;
    background: $color-tooltip;
    border-radius: 10px;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: $color-restyle-white;
    position: fixed;
    z-index: 1050;
    overflow-wrap: break-word;
    max-width: 400px;
  }

  .tooltip-tail {
    position: fixed;


    &--right {
      transform: rotate(90deg);
    }

    &--left {
      transform: rotate(-90deg);
    }

    &--bottom {
      transform: rotate(180deg);
    }

  }
</style>